import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalActions } from '@clay-run/glaze/modal';
import { Button } from '@clay-run/glaze/button';
import { Alert, AlertDescription, AlertTitle } from '@clay-run/glaze/alert';
import { Text } from '@clay-run/glaze/text';
// `/toast` has no flat `dist/toast.d.ts` — see index.tsx's import comment.
import { addApiErrorToast } from '../../_shared/api-error-toast';
import { useScanApi } from '../hooks/useScanApi';
import { useCompleteIntake } from '../hooks/useCompleteIntake';
import { decodeQrFromImageData } from '../qr-decode';
import {
  getUserMediaFailureReason,
  preflightCameraUnavailableReason,
  startCameraQrLoop,
  type CameraQrLoop,
  type CameraUnavailableReason,
} from '../camera-qr';

/**
 * "Scan LinkedIn QR" — the live-camera intake path (U12, R12 of
 * plans/2026-08-25-002-lead-scanner-round2-plan.md). A rep points the phone
 * at the LinkedIn app's share-profile QR on someone's screen (or a badge
 * carrying one) and gets a draft with the profile URL already filled in,
 * without taking a photo at all.
 *
 * This is the first `getUserMedia({video})` surface anywhere in Terra's
 * frontend, so the component is deliberately as thin as it can be: the
 * repeat-until-you-see-a-LinkedIn-profile decision lives in `camera-qr.ts`
 * (unit-tested with no camera and no WASM), the decode lives in
 * `qr-decode.ts`, and what's left here is exactly the three things only a
 * component can do — hold the `MediaStream`, paint `<video>` frames into a
 * canvas, and route the result.
 *
 * ─── Media-stream lifecycle ───────────────────────────────────────────────
 * A leaked `MediaStream` is not a memory leak, it's a camera light that
 * stays on. The rules this component follows:
 *
 *  - The stream is acquired in the open-effect and released in that same
 *    effect's cleanup — `stream.getTracks().forEach(t => t.stop())`. Nothing
 *    else in the component owns it.
 *  - `cancelled` is a **plain closure variable scoped to one effect run**,
 *    never a ref. That is what makes the StrictMode double-invoke
 *    (`setup → cleanup → setup`) safe by construction rather than by
 *    discipline: the first run's `getUserMedia` promise resolves into a
 *    closure whose `cancelled` is already `true`, so it stops its own tracks
 *    immediately and the second run's stream is the only live one. The
 *    hazard in docs/v4-system/strictmode-resilient-effects.md — lifetime
 *    state in a ref that cleanup sets and mount never resets — cannot occur
 *    here because there is no such ref to get stuck.
 *  - Closing the modal is what stops the camera: `open` is the effect's
 *    dependency, so `setOpen(false)` runs the cleanup. There is no separate
 *    "stop the camera" path that could drift out of sync with it.
 *
 * ─── Secure context ───────────────────────────────────────────────────────
 * `navigator.mediaDevices` only exists in a secure context. `localhost`
 * counts; a LAN IP (`http://192.168.x.x:5173`) does not — which is exactly
 * how a rep testing on a phone against a dev machine hits it. That case, a
 * denied permission prompt, and a device with no camera all land in the same
 * inline explanatory state with the photo-picker fallback beside it, because
 * the rep's next move is the same in all three.
 */

interface LinkedInQrScannerProps {
  campaignId: string | null;
  scannedByEmail: string | null;
  disabled?: boolean;
  /**
   * Closes the scanner and opens the badge-photo picker instead — the
   * fallback when the camera can't be used at all. The still-photo pipeline
   * already QR-decodes what it's given (U5), so a screenshot or photo of the
   * same QR reaches the same outcome by a route that needs no camera
   * permission.
   */
  onUsePhotoFallback: () => void;
}

/** Copy for each way the camera can be unavailable. Split by cause because
 *  the rep's actual next step differs: re-granting a permission, moving to a
 *  secure URL, or giving up on the camera entirely. */
const CAMERA_UNAVAILABLE_COPY: Record<CameraUnavailableReason, { title: string; hint: string }> = {
  'permission-denied': {
    title: 'Camera access is blocked',
    hint: 'Tap the lock or camera icon in your browser’s address bar, set Camera to Allow, then reopen this scanner. On iOS you may need Settings → Safari → Camera → Ask.',
  },
  'no-camera': {
    title: 'No camera found',
    hint: 'This device didn’t report a usable camera. If an external camera is plugged in, reconnect it and reopen this scanner.',
  },
  'camera-busy': {
    title: 'The camera is in use',
    hint: 'Another app or tab has the camera. Close it — video calls are the usual culprit — then reopen this scanner.',
  },
  'insecure-context': {
    title: 'Camera needs a secure connection',
    hint: 'Browsers only allow camera access over HTTPS or on localhost. Open this app on its https:// address and try again.',
  },
  unknown: {
    title: 'Couldn’t start the camera',
    hint: 'Reopen the scanner to try again. If it keeps failing, take a photo of the QR code instead — it reads the same code.',
  },
};

/**
 * Paints the current `<video>` frame into the reused canvas and hands back
 * its pixels.
 *
 * `null` covers every not-ready state, which is the common case for the
 * first few ticks: no video element mounted yet, no dimensions negotiated
 * with the camera yet, no 2D context. The loop treats a `null` frame as a
 * free no-op, so "not ready" needs no signalling beyond this.
 *
 * The canvas is created once per scanner mount and resized only when the
 * camera's own dimensions change — allocating a canvas (or a fresh
 * `ImageData`) several times a second is exactly the kind of churn that
 * makes a viewfinder stutter on an event-floor phone.
 */
function grabVideoFrame(video: HTMLVideoElement | null, canvas: HTMLCanvasElement): ImageData | null {
  if (video === null) return null;

  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width === 0 || height === 0) return null;

  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;

  // `willReadFrequently` is the whole point here — without it browsers keep
  // the canvas GPU-backed and every `getImageData` pays a readback.
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) return null;

  context.drawImage(video, 0, 0, width, height);
  return context.getImageData(0, 0, width, height);
}

export default function LinkedInQrScanner({
  campaignId,
  scannedByEmail,
  disabled,
  onUsePhotoFallback,
}: LinkedInQrScannerProps) {
  const [open, setOpen] = useState(false);
  const [unavailable, setUnavailable] = useState<CameraUnavailableReason | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  // Lazily created and reused across ticks AND across opens. A ref holding a
  // cached resource, not lifetime state — nothing about it can be left
  // stale by an effect cleanup.
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const { createScan } = useScanApi();
  const completeIntake = useCompleteIntake();

  const ready = campaignId != null && scannedByEmail != null && !disabled;

  /**
   * A LinkedIn profile QR is in view. Close the scanner first — that runs
   * the effect cleanup, which stops the camera before anything slower
   * happens — then create the draft and route to its review screen exactly
   * as every other intake path does (`completeIntake`, one id → straight to
   * review).
   *
   * `sourceType: 'photo'` with `photoFilename: null`: there is no new source
   * vocabulary for this path (the plan's call), and no file was ever
   * captured — the QR was read straight out of the video stream.
   */
  const handleResult = useCallback(
    async (linkedin: string): Promise<void> => {
      setOpen(false);
      if (campaignId === null || scannedByEmail === null) return;

      setIsCreating(true);
      try {
        const scan = await createScan({
          idempotencyKey: crypto.randomUUID(),
          sourceType: 'photo',
          status: 'draft',
          campaignId,
          scannedByEmail,
          photoFilename: null,
          linkedin,
        });
        completeIntake([scan.id]);
      } catch (err) {
        addApiErrorToast({ content: 'Scanned the QR code but couldn’t save the lead.', error: err });
      } finally {
        setIsCreating(false);
      }
    },
    [campaignId, scannedByEmail, createScan, completeIntake],
  );

  /**
   * The camera effect below must depend on `open` and NOTHING else — a
   * re-keyed effect means stopping and re-acquiring the `MediaStream`
   * mid-scan, i.e. a black viewfinder for a beat while a rep is holding a
   * phone up to someone's badge. `handleResult` legitimately changes
   * identity (its `campaignId`/`createScan` deps do), so it reaches the loop
   * through this always-current holder rather than through the dep array.
   *
   * This is NOT the ref hazard in
   * docs/v4-system/strictmode-resilient-effects.md: that one is a ref
   * carrying LIFETIME state (`cancelled`/`mounted`) written in a cleanup and
   * never reset on mount. This ref carries no lifetime state at all — it is
   * overwritten after every render and never touched by a cleanup, so there
   * is no state for a StrictMode double-invoke (or a real remount) to
   * strand. The genuine lifetime flag lives in the camera effect as a plain
   * closure variable, which is exactly where it belongs.
   *
   * Declared before the camera effect so it is already current the first
   * time the camera effect reads it (effects run in declaration order), and
   * with no dep array so "after every render" is unconditional.
   */
  const handleResultRef = useRef(handleResult);
  useEffect(() => {
    handleResultRef.current = handleResult;
  });

  useEffect(() => {
    if (!open) return;

    // Scoped to THIS effect run. See the header comment: this is the reason
    // the StrictMode double-invoke leaves exactly one live stream, and the
    // reason there is no ref here to reset.
    let cancelled = false;
    let stream: MediaStream | null = null;
    let loop: CameraQrLoop | null = null;

    function releaseStream() {
      const released = stream;
      // The camera light goes out here and nowhere else.
      released?.getTracks().forEach((track) => track.stop());
      stream = null;

      // Detach only if the element still holds THE stream we just stopped.
      // A late-resolving `getUserMedia` from a superseded effect run
      // (StrictMode, or a fast close-and-reopen) would otherwise blank the
      // live run's viewfinder — the next tick re-attaches, but a 200ms black
      // frame for no reason is worth this one identity check.
      const video = videoRef.current;
      if (video && (released === null || video.srcObject === released)) {
        video.srcObject = null;
      }
    }

    async function start(): Promise<void> {
      const blocked = preflightCameraUnavailableReason({
        isSecureContext: window.isSecureContext,
        hasGetUserMedia: typeof navigator.mediaDevices?.getUserMedia === 'function',
      });
      if (blocked !== null) {
        setUnavailable(blocked);
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // The rear camera is the one pointed at the other person's phone.
          // `facingMode` (not `exact`) so a laptop with only a front camera
          // still gets a usable stream instead of an OverconstrainedError.
          video: { facingMode: 'environment' },
        });
      } catch (err) {
        setUnavailable(getUserMediaFailureReason(err));
        return;
      }

      if (cancelled) {
        // StrictMode's first run, or the rep closing the modal during the
        // permission prompt. Either way this stream must not outlive us.
        releaseStream();
        return;
      }

      const activeStream = stream;
      canvasRef.current ??= document.createElement('canvas');
      const canvas = canvasRef.current;

      loop = startCameraQrLoop({
        grabFrame: () => {
          const video = videoRef.current;
          if (video === null) return null;
          // Attached here rather than in a second effect: the `<video>` is
          // inside the modal's portal, so its mount order relative to this
          // effect isn't ours to assume. Attaching lazily makes that a
          // non-question — until the element exists there is simply no
          // frame, which the loop already handles. Idempotent by the
          // identity check.
          if (video.srcObject !== activeStream) {
            video.srcObject = activeStream;
            // Set imperatively as well as via the JSX prop: React's `muted`
            // prop is famously unreliable, and an unmuted stream is refused
            // autoplay by every mobile browser.
            video.muted = true;
          }
          return grabVideoFrame(video, canvas);
        },
        decode: decodeQrFromImageData,
        onResult: (linkedInUrl) => {
          void handleResultRef.current(linkedInUrl);
        },
      });
    }

    void start();

    return () => {
      cancelled = true;
      loop?.cancel();
      releaseStream();
    };
  }, [open]);

  const copy = unavailable === null ? null : CAMERA_UNAVAILABLE_COPY[unavailable];

  return (
    <>
      <Button
        variant="secondary"
        size="lg"
        disabled={!ready}
        loading={isCreating}
        onClick={() => {
          // A fresh attempt every open: a rep who just fixed the permission
          // in their browser settings must not be shown the stale blocked
          // state.
          setUnavailable(null);
          setOpen(true);
        }}
      >
        Scan LinkedIn QR
      </Button>

      <Modal open={open} onOpenChange={setOpen} size="md">
        <ModalHeader onClose={() => setOpen(false)}>Scan LinkedIn QR</ModalHeader>
        <ModalBody>
          {copy !== null ? (
            <Alert status="warning">
              <AlertTitle size="sm">{copy.title}</AlertTitle>
              <AlertDescription>{copy.hint}</AlertDescription>
            </Alert>
          ) : (
            <div className="flex flex-col gap-2">
              <div className="overflow-hidden rounded-lg bg-background-secondary">
                {/* No caption track: a live camera viewfinder has nothing to
                    caption. `aria-label` names the region instead. */}
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  aria-label="Camera viewfinder"
                  className="aspect-[3/4] w-full object-cover"
                />
              </div>
              <Text size="sm" variant="tertiary">
                Point the camera at the LinkedIn share QR code. The lead is created as soon as it
                reads.
              </Text>
            </div>
          )}
        </ModalBody>
        <ModalActions>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setOpen(false);
              onUsePhotoFallback();
            }}
          >
            Take a photo instead
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}
